import { ChangeDetectorRef, Component, OnDestroy, OnInit, AfterContentChecked } from '@angular/core';
import { FormArray, FormControl, FormGroup, }
  from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppConfigService } from '@core/appconfig.service';
import { UserService } from '@core/user.service';
import { School } from '@models/school';
import { IUser, UserPartial } from '@models/user';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CreateUserFormHelpers } from '@shared/manage-users/manage-users-form/helpers/form-mappers';
import { GenericConfirmModalComponent } from '@shared/modals/generic-confirm-modal/generic-confirm-modal.component';
import { passwordRequirements } from '@shared/password-requirement-list';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { DISTRICT_ACTIONS } from '../../../districts/store/districts.actions';
import { SCHOOL_ACTIONS } from '../../../schools/store/schools.actions';
import { SchoolsFacade } from '../../../schools/store/schools.facade';
import { RoleType } from '../../enums/role-type';
import { FormHelpers } from '../../form-helpers';
import {
  generalUserFields, newUsersFormGroupName, primaryFormControlNames
} from '../../manage-users/manage-users-form/constants/form-field-setup-data';

@Component({
  selector: 'zbp-branded-create-school-users',
  templateUrl: './branded-create-school-users.component.html',
  styleUrls: ['./branded-create-school-users.component.scss'],
  standalone: false
})
export class BrandedCreateSchoolUsersComponent implements OnDestroy, OnInit, AfterContentChecked {
  backRouterTitle: string = 'Manage Users';
  pageTitle: string = 'Add New User';
  educationalUnitId: string = '';
  educationalUnitName: string = '';
  usesRoleTypeSelector = false;
  isSearching = false;
  usesOneRoster = false;
  passwordRequirements: string[] = passwordRequirements;
  confirmModal: NgbModalRef;
  school: School;
  isSaving = false;
  form: FormGroup;
  formFieldNames;
  roleTypeOptions: { label: string; value: RoleType }[] = [];

  readonly omittableProperties: string[] = [
    'confirmPassword',
    'student',
    'parentFirstName',
    'parentLastName',
    'parentUsername',
  ];

  private roleTypes: RoleType[] = [RoleType.Student, RoleType.Teacher, RoleType.Parent, RoleType.SchoolAdministrator];
  private subscriptions: Subscription[] = [];

  protected readonly zbpIconNames = zbpIconNames;
  protected readonly RoleType = RoleType;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private toastr: ToastrService,
    private schoolsFacade: SchoolsFacade,
    private appConfigService: AppConfigService,
    private ngbModal: NgbModal,
    private cdref: ChangeDetectorRef,
    private title: Title,
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  ngOnInit(): void {
    this.formFieldNames = generalUserFields;

    this.form = new FormGroup({});

    this.subscriptions.push(
      combineLatest([
        this.route.parent.params,
        this.route.queryParams,
        this.schoolsFacade.schools$,
      ])
        .subscribe(([params, queryParams, schools]: [Params, Params, School[]]) => {
          if (params.districtId && !queryParams.schoolId) {
            // Called in district context i.e. create district admins.
            this.educationalUnitId = params.districtId;
            this.backRouterTitle = 'Manage District Admins';
            this.pageTitle = 'Add District Admin';
            this.usesRoleTypeSelector = false;
            this.form.addControl('roleSelected', new FormControl(RoleType.DistrictAdministrator));

          } else if (params.schoolId || queryParams.schoolId) {
            // Called in school context so role type is required or SchoolAdministrator.
            const schoolId = params.schoolId ? params.schoolId : queryParams.schoolId;
            this.school = schools.find(s => s.schoolId === schoolId);
            this.educationalUnitId = schoolId;
            this.educationalUnitName = this.school?.name;
            this.usesRoleTypeSelector = true;
            this.backRouterTitle = 'Manage Users';
            this.usesOneRoster = this.school?.isOneRoster;

            this.roleTypeOptions = [];
            if (!this.usesOneRoster) {
              this.roleTypeOptions.push({ label: 'SCHOOL ADMIN', value: RoleType.SchoolAdministrator });
              this.roleTypeOptions.push({ label: 'TEACHER', value: RoleType.Teacher });
            }
            if (!this.isHighlightsUser()) {
              this.roleTypeOptions.push({ label: 'PARENT', value: RoleType.Parent });
            }
            if (!this.usesOneRoster && !this.isHighlightsUser()) {
              this.roleTypeOptions.push({ label: 'STUDENT', value: RoleType.Student });
            }

            let initialRole: RoleType = null;
            if (this.roleTypes.includes(queryParams.roleType)) {
              initialRole = queryParams.roleType;
            } else {
              initialRole = RoleType.SchoolAdministrator;
            }

            this.form.addControl('roleSelected', new FormControl(initialRole));

            if (!this.school?.abbreviatedSchoolId && initialRole === RoleType.Student) {
              this.toastr.error('You must set an abbreviated school id for this school prior to adding students.');
              this.form.disable();
            }
          }
        }),
    );
    this.title.setTitle(`${this.pageTitle} | ${this.appConfigService.getBrandTitle}`);
  }

  isHighlightsUser(): boolean {
    return this.appConfigService.isHighlightsPortalUrl;
  }

  isFormInvalid(): boolean {
    return this.form.invalid;
  }

  get roleSelectedValue() {
    return this.form.controls['roleSelected']?.value;
  }

  saveUsers(): void {
    if (this.isFormInvalid()) {
      const errors = FormHelpers.getAllFormErrors(this.form);
      if (errors.length > 0) {
        const headerMessage = '<div>Please correct the following errors: </div>';
        const errorMessages = errors.reduce((ret, err) => `${ret}<div>${err}</div>`, '');
        this.toastr.error(`${headerMessage}${errorMessages}`, '', { enableHtml: true });
      } else {
        this.toastr.error('Please check the form fields and try again.');
      }
    } else {
      let data: UserPartial[];
      try {
        const newUsersFormGroup = this.getNewUsersFormGroup();
        data = CreateUserFormHelpers.mapFormValuesToRequestData(newUsersFormGroup, this.school, this.educationalUnitId);
      } catch (error) {
        this.toastr.error(error.message);
        return;
      }

      const createUsersFormGroup: FormGroup = this.form.get(newUsersFormGroupName) as FormGroup;
      const createUsersArray: FormArray = createUsersFormGroup.get(
        primaryFormControlNames.newUsers
      ) as FormArray;

      // Checks the form values itself for empty password because data contains both parent and student users combined.
      const hasEmptyPassword = this.roleSelectedValue === RoleType.Student
        && createUsersArray.value.findIndex(values => !values.password) !== -1;

      let save$: Observable<boolean>;

      if (this.roleSelectedValue === RoleType.Student && hasEmptyPassword) {
        const modalRef = GenericConfirmModalComponent.open(this.ngbModal, {
          title: 'Are you sure you want to proceed without adding passwords?',
          body:
            'If your students will be logging into ZB Portal directly from the login page, they will need a password.'
            + ' If your students are using single sign-on, a password will not be necessary.',
          options: { size: 'lg' },
        });

        save$ = modalRef.closed;
      } else {
        save$ = of(true);
      }

      save$
        .pipe(
          catchError(() => of(false)),
          mergeMap((confirm) => {
            if (confirm) {
              this.isSaving = true;
              return this.userService.upsertUsers(data);
            }
            return of(null);
          })
        )
        .subscribe({
          next: async (res) => {
            if (res) {
              // ZBPortal-Api is all or nothing.
              if (res.success) {
                createUsersFormGroup.get(primaryFormControlNames.newUsers)?.reset();
                createUsersFormGroup.get(primaryFormControlNames.roleType)?.reset();

                if (this.school?.districtId && this.school.districtId.length > 0) {
                  this.schoolsFacade.dispatch({ type: DISTRICT_ACTIONS.refresh });
                } else {
                  this.schoolsFacade.dispatch({ type: SCHOOL_ACTIONS.refresh });
                }

                await this.router.navigate(this.backRouterLink, { relativeTo: this.route }).then(() => {
                  setTimeout(() => {
                    res.response.forEach((u: IUser) => {
                      this.toastr.success(`${u.getDisplayName} added successfully`);
                    });
                  }, 2000);
                });

              } else {
                this.toastr.error(res.messages[0]);

                if (res.response) {
                  // Support partial successes if the Api ever supports it like the legacy Api.
                  data.forEach((values, index) => {
                    const saved = res.response.findIndex(u => u.username === values.username);
                    if (saved !== -1) {
                      (createUsersFormGroup
                        .get(primaryFormControlNames.newUsers) as FormArray)
                        .removeAt(index);
                    }
                  });
                }
              }
            }
            this.isSaving = false;
          },
          error: (error) => {
            this.isSaving = false;
            throw (error);
          }
        });
    }
  }

  cancel() {
    this.router.navigate(this.backRouterLink, { relativeTo: this.route });
  }

  getNewUsersFormGroup(): FormGroup {
    const createUsersFormGroup = this.form.get(newUsersFormGroupName) as FormGroup;

    return createUsersFormGroup;
  }

  get backRouterLink(): string[] {
    if (this.usesRoleTypeSelector) {
      const path = ['../users'];
      if (this.roleSelectedValue === RoleType.SchoolAdministrator) {
        return path.concat('admins');
      }
      return path.concat(`${this.roleSelectedValue.toLocaleLowerCase()}s`);
    }
    return ['../admins'];
  }

  goBack() {
    this.router.navigate(this.backRouterLink, { relativeTo: this.route });
  }
}
